import { makeEventNamespace } from "~/core/dom/events";
import { initSection } from "~/core/shopify/init-section";
import {
    SYSTEM_SELECT_EVENT,
    getSystemRows,
    selectSystemRow,
    selectedRow,
} from "~/scripts/sections/system-rows";
import "~/styles/sections/system-finder.css";

/*
 * Concern Level (below 1024px). The squares and labels render with the page. This names each
 * square after its system, opens that system's row on tap, and follows the row that is open.
 */
initSection(".js-systems-intro-section", (section) => {
    const namespace = makeEventNamespace();
    const group = section.querySelector<HTMLElement>("[data-systems-steps]");
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    const steps = (): HTMLButtonElement[] =>
        Array.from(
            group?.querySelectorAll<HTMLButtonElement>("[data-systems-step]") ??
                [],
        );

    const setActive = (index: number): void => {
        steps().forEach((step, stepIndex) => {
            const active = stepIndex === index;
            step.classList.toggle("is-active", active);
            step.setAttribute("aria-pressed", String(active));
        });
    };

    const syncWithRows = (): void => {
        const rows = getSystemRows();
        steps().forEach((step, index) => {
            const row = rows[index];
            if (row === undefined) return;
            const name = row.dataset.systemName || `System ${index + 1}`;
            const bestFor = row.dataset.concernLabel;
            step.setAttribute(
                "aria-label",
                bestFor ? `${name}: ${bestFor}` : name,
            );
        });
        const openIndex = rows.findIndex((row) =>
            row.classList.contains("is-open"),
        );
        setActive(Math.max(openIndex, 0));
    };

    if (group !== null) {
        namespace.addDelegatedEventListener(
            group,
            "[data-systems-step]",
            "click",
            (step) => {
                const row = getSystemRows()[Number(step.dataset.systemsStep)];
                if (row === undefined) return;
                selectSystemRow(row);
                row.scrollIntoView({
                    behavior: reduceMotion.matches ? "auto" : "smooth",
                    block: "nearest",
                });
            },
        );
    }

    const onSelect = (event: Event): void => {
        const row = selectedRow(event);
        if (row === null) return;
        const index = getSystemRows().indexOf(row);
        if (index >= 0) setActive(index);
    };

    document.addEventListener(SYSTEM_SELECT_EVENT, onSelect);
    syncWithRows();

    return {
        unload: () => {
            namespace.destroy();
            document.removeEventListener(SYSTEM_SELECT_EVENT, onSelect);
        },
    };
});
